import { useState, lazy, Suspense, useEffect } from 'react';
import { apiFetch } from '../api';
import { useLocation } from 'react-router-dom';
import Button from '../components/ui/Button';
import { hasPermissionSync, loadPermissions } from '../auth/permissions';
const PaymentMethodsAdmin = lazy(() => import('./admin/PaymentMethodsAdmin'));
// Collections admin removed from UI
const UsersAdmin = lazy(() => import('./admin/UsersAdminExtended'));
const OrgsAdmin = lazy(() => import('./admin/OrgsAdmin'));
const LogoAdmin = lazy(() => import('./admin/LogoAdmin'));
const ModulesAdmin = lazy(() => import('./admin/Modules'));
const UserChangeRequests = lazy(() => import('./admin/UserChangeRequests'));
const KioskTokenGenerator = lazy(() => import('./license-control/KioskTokenGenerator'));

const RolesAdmin = lazy(() => import('./admin/RolesAdmin'));

export default function Admin() {
  const location = useLocation();
  const hash = (location.hash || '').replace('#', '');
  const [tab, setTab] = useState<'users' | 'orgs' | 'roles' | 'logo' | 'modules' | 'payments' | 'change-requests' | 'externe-geraete'>(['users', 'orgs', 'roles', 'logo', 'modules', 'payments', 'change-requests', 'externe-geraete'].includes(hash) ? (hash as any) : 'users');
  const [allowed, setAllowed] = useState<string[] | null>(null);
  const [permsLoaded, setPermsLoaded] = useState(false);
  const [orgs, setOrgs] = useState<{id:number;short_code:string;name:string}[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<string | null>(localStorage.getItem('organization'));
  const token = localStorage.getItem('token');

  useEffect(() => {
    loadPermissions().finally(() => setPermsLoaded(true));
  }, []);

  useEffect(() => {
    const org = localStorage.getItem('organization');
    if (org === 'SUPER') {
      setAllowed(['*']);
      // load org list for super admin to switch context
      apiFetch('/admin/orgs')
        .then((o) => setOrgs(o || []))
        .catch(() => setOrgs([]));
      return;
    }
    if (!token) return setAllowed([]);
    apiFetch('/admin/organization-modules')
      .then((rows) => setAllowed((rows || []).map((r: any) => r.module_code)))
      .catch(() => setAllowed([]));
  }, [token]);

  function isAllowed(code: string) {
    if (!allowed) return false;
    if (allowed.includes('*')) return true;
    return allowed.includes(code);
  }

  // Tab → erforderliche Berechtigung (für Nicht-SUPER-Organisationen).
  // null = keine zusätzliche Berechtigung nötig (Tab ist ohnehin SUPER-only oder
  // wird über eine eigene Bedingung geprüft).
  const TAB_PERMISSIONS: Record<string, string | string[] | null> = {
    users: 'users:view',
    roles: 'roles:manage',
    'change-requests': ['change_requests:view', 'users:change_requests', 'users:edit'],
    orgs: 'admin:orgs:view',
    modules: 'admin:modules:view',
    logo: ['logo:view', 'logo:manage'],
    payments: ['payments:view', 'payments:manage'],
    'externe-geraete': ['external-devices:view', 'external-devices:manage'],
  };

  function hasTabPermission(t: string): boolean {
    if (localStorage.getItem('organization') === 'SUPER') return true;
    const req = TAB_PERMISSIONS[t];
    if (req == null) return true;
    if (!permsLoaded) return false;
    const codes = Array.isArray(req) ? req : [req];
    return codes.some((c) => hasPermissionSync(c));
  }

  const noAccess = (
    <div className="p-6 bg-white dark:bg-gray-800 rounded border border-red-300 text-red-700 dark:text-red-300">
      <h2 className="text-lg font-semibold mb-2">Keine Berechtigung</h2>
      <p className="text-sm">Sie haben keine Berechtigung, diese Seite zu öffnen. Bitte wenden Sie sich an Ihren Organisations-Administrator.</p>
    </div>
  );

  // Keep `tab` in sync with the URL hash when it changes (clicks in the sidebar change the hash).
  // Previously we only read the hash once at mount which caused the Admin view to not update
  // when navigating between admin subpages using hash fragments.
  useEffect(() => {
    const h = (location.hash || '').replace('#', '');
    if (['users', 'orgs', 'roles', 'logo', 'modules', 'payments', 'change-requests', 'externe-geraete'].includes(h)) {
      setTab(h as any);
    }
  }, [location.hash]);
  function switchOrg(code: string | null) {
    if (!code) return;
    localStorage.setItem('organization', code);
    // reload to let app pick up organization change
    window.location.reload();
  }

  function isSuperAdmin(): boolean {
    const token = localStorage.getItem('token');
    if (!token) return false;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const roles: string[] = payload.roles || [];
      return roles.includes('SuperAdmin');
    } catch {
      return false;
    }
  }

  return (
    <div className="p-2 sm:p-4 md:p-6 max-w-full overflow-x-hidden">
      {tab === 'users' && isAllowed('users') && (
        hasTabPermission('users') ? (
          <Suspense fallback={<div>Lade Benutzer...</div>}>
            <UsersAdmin />
          </Suspense>
        ) : noAccess
      )}
      {tab === 'orgs' && isAllowed('orgs') && (
        <Suspense fallback={<div>Lade Organisationen...</div>}>
          <OrgsAdmin />
        </Suspense>
      )}
      {tab === 'modules' && localStorage.getItem('organization') === 'SUPER' && (
        <Suspense fallback={<div>Lade Module...</div>}>
          <ModulesAdmin />
        </Suspense>
      )}
      {tab === 'payments' && isAllowed('orgs') && (
        <Suspense fallback={<div>Lade Zahlungsmittel...</div>}>
          <PaymentMethodsAdmin />
        </Suspense>
      )}
      {tab === 'roles' && isAllowed('roles') && !hasTabPermission('roles') && noAccess}
      {tab === 'roles' && isAllowed('roles') && hasTabPermission('roles') && (
        <Suspense fallback={<div>Lade Rollen...</div>}>
          <RolesAdmin />
        </Suspense>
      )}
      {tab === 'logo' && !hasTabPermission('logo') && noAccess}
      {tab === 'logo' && hasTabPermission('logo') && (
        <Suspense fallback={<div>Lade Logos...</div>}>
          <LogoAdmin />
        </Suspense>
      )}
      {tab === 'change-requests' && isAllowed('users') && (
        hasTabPermission('change-requests') ? (
          <Suspense fallback={<div>Lade Änderungsanträge...</div>}>
            <UserChangeRequests />
          </Suspense>
        ) : noAccess
      )}
      {tab === 'externe-geraete' && (
        <Suspense fallback={<div>Lade Externe Geräte...</div>}>
          <KioskTokenGenerator />
        </Suspense>
      )}
    </div>
  );
}
